import { difference, intersection, isEqual, isEqualWith, isNil } from 'lodash';
import moment from 'moment'; // alias to `dayjs`

import { GetValueCallback, IMatcher } from './IMatcher';
import { ICondition, Operator } from './ICondition';

import { LookupOption, OptionRecord } from '../../types/models/user-management';
import { LookupType } from '../../types/models/lookup';

export class MatchAnswer implements IMatcher {
  readonly condition: ICondition;
  readonly lookupsData: OptionRecord | undefined;

  constructor(condition: ICondition, lookupsData?: OptionRecord) {
    this.condition = condition;
    this.lookupsData = lookupsData;
  }

  findValue(lookupList: LookupOption[], label: string) {
    const option = lookupList.find(({ displayText }) => displayText?.trim() === label.trim());
    return option?.id || label;
  }

  getValueBylabel(label: string | string[], lookup: string) {
    let mappedLookup = lookup;

    if (mappedLookup === LookupType.CLASS_LEVELS) {
      mappedLookup = 'classLevels';
    }

    if (!this.lookupsData || !this.lookupsData[mappedLookup]) {
      return label;
    }
    const lookupList = this.lookupsData[mappedLookup];
    if (Array.isArray(label)) {
      return label.map((label) => this.findValue(lookupList, label));
    } else {
      return this.findValue(lookupList, label);
    }
  }

  customizer(obj1: any, obj2: any): boolean {
    if (moment.isMoment(obj1)) {
      return obj1.isSame(moment(obj2));
    }
    return isEqual(obj1, obj2);
  }

  isEqual(obj1: any, obj2: any): boolean {
    return isEqualWith(obj1, obj2, this.customizer);
  }

  isGreaterThan(obj1: any, obj2: any): boolean {
    if (moment.isMoment(obj1)) {
      return obj1.isAfter(moment(obj2));
    }
    return obj1 > obj2;
  }

  isLessThan(obj1: any, obj2: any): boolean {
    if (moment.isMoment(obj1)) {
      return obj1.isBefore(moment(obj2));
    }
    return obj1 < obj2;
  }

  match(getValue: GetValueCallback): boolean {
    const { value, label, fieldName, lookup } = this.condition;
    const currentValue = getValue(fieldName);
    const conditionValue = this.lookupsData && label && lookup ? this.getValueBylabel(label, lookup) : value;

    if (isNil(currentValue)) {
      return false;
    }

    if (this.condition.operator === Operator.EQUAL) {
      return this.isEqual(currentValue, conditionValue);
    } else if (this.condition.operator === Operator.NOT_EQUAL) {
      return !this.isEqual(currentValue, conditionValue);
    } else if (this.condition.operator === Operator.GREATER_THAN) {
      return this.isGreaterThan(currentValue, conditionValue);
    } else if (this.condition.operator === Operator.LESS_THAN) {
      return this.isLessThan(currentValue, conditionValue);
    } else if (this.condition.operator === Operator.ONE_OF) {
      const currentValueArr = Array.isArray(currentValue) ? currentValue : [currentValue];
      return Array.isArray(conditionValue) && intersection(currentValueArr, conditionValue).length > 0;
    } else if (this.condition.operator === Operator.NOT_ONE_OF) {
      const currentValueArr = Array.isArray(currentValue) ? currentValue : [currentValue];
      return Array.isArray(conditionValue) && intersection(currentValueArr, conditionValue).length === 0;
    } else if (this.condition.operator === Operator.CONTAINS) {
      return (
        Array.isArray(currentValue) &&
        Array.isArray(conditionValue) &&
        conditionValue.length > 0 &&
        difference(conditionValue, currentValue).length === 0
      );
    } else if (this.condition.operator === Operator.NOT_CONTAINS) {
      return Array.isArray(conditionValue) && difference(conditionValue, currentValue).length > 0;
    }

    return false;
  }
}
